/**
 * Disposal prices page — pure helpers (Phase 28 / task 28.5a, DEC-331).
 *
 * Prices are what the company PAYS each disposal place per barrel, in hundredths of a cent
 * ($0.425 = 4250, D-2). Typed dollars go through the shared exact parser, never a float
 * multiply; a fifth decimal is refused rather than rounded.
 */

import {
  formatHundredthsCentAsDollars,
  parseDollarsToHundredthsCent,
} from '@lodex-pro/shared/money';
import { groupDigits } from '@lodex-pro/shared/number-format';

/** SWD A2: WELL is a catalog disposal well this company authorized (0210). */
export type DisposalPlaceKind = 'FACILITY' | 'LOCATION' | 'WELL';
export type DriverDisplay = 'FULL_COST' | 'PRIORITY_ONLY' | 'HIDDEN';
export type DispatcherDisplay = 'FULL_COST' | 'PRIORITY_ONLY';

export interface DisposalPrice {
  readonly id: string;
  readonly kind: DisposalPlaceKind;
  readonly targetId: string;
  readonly targetName: string | null;
  readonly rateHundredthsCentPerBbl: number;
  readonly effectiveFrom: string;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly isCurrent: boolean;
}

export interface DisposalPricesResponse {
  readonly today: string;
  readonly prices: readonly DisposalPrice[];
}

export interface DisposalPlace {
  readonly kind: DisposalPlaceKind;
  readonly id: string;
  readonly name: string;
  readonly currentRateHundredthsCentPerBbl: number | null;
}

export interface DisposalRecSettings {
  readonly enabled: boolean;
  readonly driverDisplay: DriverDisplay;
  readonly dispatcherDisplay: DispatcherDisplay;
  readonly typicalLoadBbl: number;
  readonly fuelCostCentsPerMile: number | null;
  readonly driverCostCentsPerHour: number | null;
}

export interface AddPriceBody {
  readonly target: { readonly kind: DisposalPlaceKind; readonly id: string };
  readonly rateHundredthsCentPerBbl: number;
  readonly effectiveFrom: string;
  readonly notes: string | null;
}

/** "$0.425 / bbl" — the exact stored value, two to four decimals. */
export function formatPricePerBbl(units: number): string {
  return `$${groupDigits(formatHundredthsCentAsDollars(units))} / bbl`;
}

export type PriceInput =
  | { readonly ok: true; readonly units: number }
  | { readonly ok: false; readonly message: string };

/** The price box → hundredths of a cent, or the reason it can't be saved. */
export function readPriceInput(raw: string): PriceInput {
  const r = parseDollarsToHundredthsCent(raw);
  if (r.ok) {
    if (r.hundredthsCent > 1_000_000) return { ok: false, message: 'That is over $100 a barrel.' };
    return { ok: true, units: r.hundredthsCent };
  }
  switch (r.reason) {
    case 'blank':
      return { ok: false, message: 'Enter the price per barrel.' };
    case 'too-precise':
      return { ok: false, message: 'Use at most four decimals, like 0.4250.' };
    default:
      return { ok: false, message: 'Enter a price like 0.45 or 0.425.' };
  }
}

/** Why the switch can't be turned on yet, in words, or null when it can (D-4). */
export function missingCostsMessage(s: DisposalRecSettings): string | null {
  const missing = [
    ...(s.fuelCostCentsPerMile === null ? ['fuel cost per mile'] : []),
    ...(s.driverCostCentsPerHour === null ? ['driver cost per hour'] : []),
  ];
  return missing.length === 0
    ? null
    : `Set the ${missing.join(' and ')} in dispatch Settings → Jobs, products & pricing before turning this on.`;
}

export const DRIVER_DISPLAY_LABELS: Record<DriverDisplay, string> = {
  FULL_COST: 'Full costs',
  PRIORITY_ONLY: 'Ranking only, no dollars',
  HIDDEN: 'Nothing (just the assigned place)',
};

export const DISPATCHER_DISPLAY_LABELS: Record<DispatcherDisplay, string> = {
  FULL_COST: 'Full costs',
  PRIORITY_ONLY: 'Ranking only, no dollars',
};
